import {
  MatcherAnswers,
  MatcherCheckboxOption,
  QuestionId,
  getAnsweredString,
  getAnsweredValue
} from './types';
import { buildStepQueue } from './buildStepQueue';
import {
  SERVICES_OPTIONS,
  BUSINESS_OPTIONS,
  ENTITY_OPTIONS,
  PROBLEM_OPTIONS,
  PLAN_OPTIONS,
  FILE_OPTIONS
} from './checkboxOptions';
import { INDUSTRY_NICHE_CODE_BY_OPTION } from './industryOptions';

// The AI-tag category rt-domain-pro-service uses for industry filtering - a flat top-level key,
// not nested under whoTheyServe like filerType/situationModifiers are.
const INDUSTRY_NICHE_CATEGORY = 'industryNiche';

// Every checkbox question's options, keyed by QuestionId - the one place buildSearchTerms looks
// up a selected option's AI-tag term values. Meeting and Question are deliberately absent -
// contact-channel preference has no AI-tag field, and Question's answer is free text for a
// human to read, not a filter. Industry is absent too - a single code answer, not a
// selected-ids array, so it can't share this loop; handled separately below.
const QUESTION_OPTIONS: Partial<Record<QuestionId, MatcherCheckboxOption[]>> = {
  services: SERVICES_OPTIONS,
  business: BUSINESS_OPTIONS,
  entity: ENTITY_OPTIONS,
  problem: PROBLEM_OPTIONS,
  plan: PLAN_OPTIONS,
  file: FILE_OPTIONS
};

export function buildSearchTerms(
  answers: MatcherAnswers
): Record<string, string[]> {
  const terms: Record<string, string[]> = {};
  // A branch question (business/entity/problem/plan) can stay in `answers` after the
  // fan backs up and picks a different services option - updateMatcherAnswer only ever merges
  // in the answer for the question it's given, it never clears an abandoned branch's stale
  // answer. Gating on the current stepQueue is what stops that stale answer from still
  // contributing a term for a branch the fan's final path never actually confirmed.
  const activeStepIds = new Set<string>(buildStepQueue(answers));

  for (const [questionId, options] of Object.entries(QUESTION_OPTIONS) as [
    QuestionId,
    MatcherCheckboxOption[]
  ][]) {
    if (!activeStepIds.has(questionId)) continue;

    const selectedValue = getAnsweredValue(answers[questionId]);
    const selectedIds = Array.isArray(selectedValue) ? selectedValue : [];

    for (const option of options) {
      if (!selectedIds.includes(option.id)) continue;

      for (const { category, code } of option.termValues) {
        terms[category] ??= [];
        if (!terms[category].includes(code)) terms[category].push(code);
      }
    }
  }

  if (activeStepIds.has('industry')) {
    const industryCode =
      INDUSTRY_NICHE_CODE_BY_OPTION[getAnsweredString(answers.industry)];
    if (industryCode) {
      terms[INDUSTRY_NICHE_CATEGORY] = [industryCode];
    }
  }

  return terms;
}

// Flattens buildSearchTerms' object-of-arrays into search_terms' "category:code" string list.
export function buildMatcherSearchTermsList(answers: MatcherAnswers): string[] {
  return Object.entries(buildSearchTerms(answers)).flatMap(
    ([category, codes]) => codes.map((code) => `${category}:${code}`)
  );
}

// Fingerprints the exact inputs a match search depends on, so MatcherContextProvider's
// runMatchSearch can tell "these are the same answers as the last search" from "the fan changed
// something since" without comparing every field by hand.
export function buildMatcherSearchKey(
  postalCode: string,
  answers: MatcherAnswers
): string {
  return JSON.stringify({ postalCode, terms: buildSearchTerms(answers) });
}
